// @ts-nocheck
/**
 * Enterprise Privacy Policy Configuration Component
 * Implements comprehensive privacy controls for enterprise administrators
 */

import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Tile,
  Stack,
  Heading,
  Button,
  Form,
  FormGroup,
  TextInput,
  TextArea,
  Select,
  SelectItem,
  Toggle,
  Checkbox,
  InlineNotification,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Modal,
  ProgressIndicator,
  ProgressStep,
  Tag,
  Link,
  Accordion,
  AccordionItem,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  Loading,
  Theme,
} from '@carbon/react';
import {
  Security,
  DocumentProtected,
  Analytics,
  Warning,
  CheckmarkFilled,
  Edit,
  TrashCan,
  Add,
  Download,
  View,
  Settings,
  UserAdmin,
  Enterprise,
} from '@carbon/icons-react';
import axios from 'axios';

// API base URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface PrivacyPolicy {
  id: string;
  name: string;
  description: string;
  status: 'draft' | 'active' | 'archived';
  version: string;
  created_at: string;
  updated_at: string;
  compliance_frameworks: string[];
  auto_enforcement: boolean;
  notification_settings: {
    violations: boolean;
    policy_updates: boolean;
    compliance_reports: boolean;
  };
}

interface ComplianceViolation {
  id: string;
  policy_id: string;
  policy_name: string;
  violation_type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  user_id?: string;
  detected_at: string;
  resolved_at?: string;
  status: 'open' | 'investigating' | 'resolved' | 'false_positive';
}

interface ComplianceMetrics {
  total_policies: number;
  active_policies: number;
  compliance_score: number;
  violations_this_month: number;
  resolved_violations: number;
  pending_violations: number;
}

const EnterprisePrivacy: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [policies, setPolicies] = useState<PrivacyPolicy[]>([]);
  const [violations, setViolations] = useState<ComplianceViolation[]>([]);
  const [metrics, setMetrics] = useState<ComplianceMetrics>({
    total_policies: 0,
    active_policies: 0,
    compliance_score: 0,
    violations_this_month: 0,
    resolved_violations: 0,
    pending_violations: 0,
  });
  const [isCreatePolicyModalOpen, setIsCreatePolicyModalOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<PrivacyPolicy | null>(null);
  const [operationInProgress, setOperationInProgress] = useState(false);

  // Form state for new policy creation
  const [newPolicy, setNewPolicy] = useState({
    name: '',
    description: '',
    compliance_frameworks: [] as string[],
    auto_enforcement: true,
    notification_settings: {
      violations: true,
      policy_updates: true,
      compliance_reports: false,
    },
  });

  useEffect(() => {
    fetchPrivacyData();
  }, []);

  const fetchPrivacyData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');

      // Fetch policies, violations, and metrics in parallel
      const [policiesResponse, violationsResponse, metricsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/v1/enterprise/privacy/policies`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_BASE_URL}/v1/enterprise/privacy/violations`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_BASE_URL}/v1/enterprise/privacy/metrics`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (policiesResponse.data.success) {
        setPolicies(policiesResponse.data.data || []);
      }

      if (violationsResponse.data.success) {
        setViolations(violationsResponse.data.data || []);
      }

      if (metricsResponse.data.success) {
        setMetrics(metricsResponse.data.data || metrics);
      }
    } catch (error) {
      console.error('Failed to fetch privacy data:', error);
      // Initialize with mock data for demo
      setPolicies([
        {
          id: '1',
          name: 'GDPR Compliance Policy',
          description: 'Comprehensive GDPR compliance framework for EU data protection',
          status: 'active',
          version: '1.2.0',
          created_at: '2024-01-15T10:30:00Z',
          updated_at: '2024-03-10T14:20:00Z',
          compliance_frameworks: ['GDPR', 'ISO 27001'],
          auto_enforcement: true,
          notification_settings: {
            violations: true,
            policy_updates: true,
            compliance_reports: true,
          },
        },
        {
          id: '2',
          name: 'COPPA Student Privacy',
          description: "Children's Online Privacy Protection Act compliance for student data",
          status: 'active',
          version: '1.0.0',
          created_at: '2024-02-01T09:15:00Z',
          updated_at: '2024-02-01T09:15:00Z',
          compliance_frameworks: ['COPPA', 'FERPA'],
          auto_enforcement: true,
          notification_settings: {
            violations: true,
            policy_updates: false,
            compliance_reports: true,
          },
        },
      ]);

      setViolations([
        {
          id: '1',
          policy_id: '1',
          policy_name: 'GDPR Compliance Policy',
          violation_type: 'Data Retention Exceeded',
          severity: 'medium',
          description: 'User data retained beyond the specified retention period',
          user_id: 'user_123',
          detected_at: '2024-03-15T11:30:00Z',
          status: 'investigating',
        },
      ]);

      setMetrics({
        total_policies: 2,
        active_policies: 2,
        compliance_score: 94.5,
        violations_this_month: 3,
        resolved_violations: 2,
        pending_violations: 1,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePolicy = async () => {
    try {
      setOperationInProgress(true);
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');

      const response = await axios.post(`${API_BASE_URL}/v1/enterprise/privacy/policies`, newPolicy, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setIsCreatePolicyModalOpen(false);
        setNewPolicy({
          name: '',
          description: '',
          compliance_frameworks: [],
          auto_enforcement: true,
          notification_settings: {
            violations: true,
            policy_updates: true,
            compliance_reports: false,
          },
        });
        fetchPrivacyData();
      }
    } catch (error) {
      console.error('Failed to create policy:', error);
    } finally {
      setOperationInProgress(false);
    }
  };

  const handleResolveViolation = async (violationId: string) => {
    try {
      setOperationInProgress(true);
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');

      await axios.patch(
        `${API_BASE_URL}/v1/enterprise/privacy/violations/${violationId}`,
        { status: 'resolved' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      fetchPrivacyData();
    } catch (error) {
      console.error('Failed to resolve violation:', error);
    } finally {
      setOperationInProgress(false);
    }
  };

  const policyTableHeaders = [
    { key: 'name', header: 'Policy Name' },
    { key: 'status', header: 'Status' },
    { key: 'frameworks', header: 'Compliance Frameworks' },
    { key: 'version', header: 'Version' },
    { key: 'updated', header: 'Last Updated' },
    { key: 'actions', header: 'Actions' },
  ];

  const policyTableRows = policies.map(policy => ({
    id: policy.id,
    name: (
      <Stack gap={1}>
        <p style={{ fontWeight: 'var(--cds-font-weight-semibold)' }}>{policy.name}</p>
        <p
          style={{
            fontSize: 'var(--cds-helper-text-01-font-size)',
            color: 'var(--cds-text-secondary)',
          }}
        >
          {policy.description}
        </p>
      </Stack>
    ),
    status: (
      <Tag type={policy.status === 'active' ? 'green' : policy.status === 'draft' ? 'blue' : 'gray'}>
        {policy.status.toUpperCase()}
      </Tag>
    ),
    frameworks: (
      <Stack orientation="horizontal" gap={2}>
        {policy.compliance_frameworks.map(framework => (
          <Tag key={framework} type="outline" size="sm">
            {framework}
          </Tag>
        ))}
      </Stack>
    ),
    version: policy.version,
    updated: new Date(policy.updated_at).toLocaleDateString(),
    actions: (
      <Stack orientation="horizontal" gap={2}>
        <Button kind="ghost" size="sm" renderIcon={Edit} onClick={() => setSelectedPolicy(policy)}>
          Edit
        </Button>
        <Button kind="ghost" size="sm" renderIcon={View}>
          View
        </Button>
      </Stack>
    ),
  }));

  const violationTableHeaders = [
    { key: 'policy', header: 'Policy' },
    { key: 'type', header: 'Violation Type' },
    { key: 'severity', header: 'Severity' },
    { key: 'detected', header: 'Detected' },
    { key: 'status', header: 'Status' },
    { key: 'actions', header: 'Actions' },
  ];

  const violationTableRows = violations.map(violation => ({
    id: violation.id,
    policy: violation.policy_name,
    type: violation.violation_type,
    severity: (
      <Tag
        type={
          violation.severity === 'critical'
            ? 'red'
            : violation.severity === 'high'
              ? 'magenta'
              : violation.severity === 'medium'
                ? 'yellow'
                : 'blue'
        }
      >
        {violation.severity.toUpperCase()}
      </Tag>
    ),
    detected: new Date(violation.detected_at).toLocaleDateString(),
    status: (
      <Tag
        type={
          violation.status === 'open'
            ? 'red'
            : violation.status === 'investigating'
              ? 'yellow'
              : violation.status === 'resolved'
                ? 'green'
                : 'gray'
        }
      >
        {violation.status.toUpperCase()}
      </Tag>
    ),
    actions:
      violation.status === 'open' || violation.status === 'investigating' ? (
        <Button
          kind="primary"
          size="sm"
          onClick={() => handleResolveViolation(violation.id)}
          disabled={operationInProgress}
        >
          Resolve
        </Button>
      ) : (
        <span style={{ color: 'var(--cds-text-secondary)' }}>Resolved</span>
      ),
  }));

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
        }}
      >
        <Stack gap={4}>
          <Loading />
          <p style={{ color: 'var(--cds-text-secondary)' }}>Loading privacy management...</p>
        </Stack>
      </div>
    );
  }

  return (
    <Theme theme="g100">
      <div>
        {/* Header */}
        <div style={{ marginBottom: 'var(--cds-spacing-06)' }}>
          <Stack gap={4}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Heading size="xl">Enterprise Privacy Management</Heading>
              <Button kind="primary" renderIcon={Add} onClick={() => setIsCreatePolicyModalOpen(true)}>
                Create Policy
              </Button>
            </div>
            <p
              style={{
                color: 'var(--cds-text-secondary)',
                fontSize: 'var(--cds-body-01-font-size)',
              }}
            >
              Configure privacy policies, monitor compliance, and manage violation detection for enterprise-level data
              protection.
            </p>
          </Stack>
        </div>

        {/* Compliance Overview */}
        <Grid narrow style={{ marginBottom: 'var(--cds-spacing-06)' }}>
          <Column sm={4} md={2} lg={4}>
            <Tile style={{ padding: 'var(--cds-spacing-05)', textAlign: 'center' }}>
              <Enterprise size={32} style={{ marginBottom: 'var(--cds-spacing-03)' }} />
              <Heading size="lg">{metrics.total_policies}</Heading>
              <p style={{ color: 'var(--cds-text-secondary)' }}>Total Policies</p>
            </Tile>
          </Column>
          <Column sm={4} md={2} lg={4}>
            <Tile style={{ padding: 'var(--cds-spacing-05)', textAlign: 'center' }}>
              <Security size={32} style={{ marginBottom: 'var(--cds-spacing-03)' }} />
              <Heading size="lg">{metrics.compliance_score}%</Heading>
              <p style={{ color: 'var(--cds-text-secondary)' }}>Compliance Score</p>
            </Tile>
          </Column>
          <Column sm={4} md={2} lg={4}>
            <Tile style={{ padding: 'var(--cds-spacing-05)', textAlign: 'center' }}>
              <Warning size={32} style={{ marginBottom: 'var(--cds-spacing-03)' }} />
              <Heading size="lg">{metrics.pending_violations}</Heading>
              <p style={{ color: 'var(--cds-text-secondary)' }}>Pending Violations</p>
            </Tile>
          </Column>
          <Column sm={4} md={2} lg={4}>
            <Tile style={{ padding: 'var(--cds-spacing-05)', textAlign: 'center' }}>
              <CheckmarkFilled size={32} style={{ marginBottom: 'var(--cds-spacing-03)' }} />
              <Heading size="lg">{metrics.resolved_violations}</Heading>
              <p style={{ color: 'var(--cds-text-secondary)' }}>Resolved This Month</p>
            </Tile>
          </Column>
        </Grid>

        {/* Main Content Tabs */}
        <Tabs selectedIndex={activeTab} onChange={e => setActiveTab(e.selectedIndex)}>
          <TabList aria-label="Privacy management tabs">
            <Tab>Policy Configuration</Tab>
            <Tab>Compliance Monitoring</Tab>
            <Tab>Violation Management</Tab>
            <Tab>Settings</Tab>
          </TabList>
          <TabPanels>
            {/* Policy Configuration Tab */}
            <TabPanel>
              <div style={{ padding: 'var(--cds-spacing-05)' }}>
                <InlineNotification
                  title="Privacy Policy Management"
                  subtitle="Configure and manage enterprise privacy policies with automated compliance monitoring."
                  kind="info"
                  lowContrast
                  style={{ marginBottom: 'var(--cds-spacing-05)' }}
                />

                <DataTable headers={policyTableHeaders} rows={policyTableRows}>
                  {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                    <TableContainer>
                      <Table {...getTableProps()}>
                        <TableHead>
                          <TableRow>
                            {headers.map(header => (
                              <TableHeader {...getHeaderProps({ header })} key={header.key}>
                                {header.header}
                              </TableHeader>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.map(row => (
                            <TableRow {...getRowProps({ row })} key={row.id}>
                              {row.cells.map(cell => (
                                <TableCell key={cell.id}>{cell.value}</TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </DataTable>
              </div>
            </TabPanel>

            {/* Compliance Monitoring Tab */}
            <TabPanel>
              <div style={{ padding: 'var(--cds-spacing-05)' }}>
                <Stack gap={5}>
                  <InlineNotification
                    title="Real-time Compliance Monitoring"
                    subtitle="Monitor compliance status across all active privacy policies and frameworks."
                    kind="success"
                    lowContrast
                  />

                  <Grid narrow>
                    <Column lg={8}>
                      <Tile style={{ padding: 'var(--cds-spacing-05)' }}>
                        <Heading size="md" style={{ marginBottom: 'var(--cds-spacing-05)' }}>
                          Compliance Framework Status
                        </Heading>
                        <Stack gap={4}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>GDPR Compliance</span>
                            <Tag type="green">95% Compliant</Tag>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>COPPA Compliance</span>
                            <Tag type="green">98% Compliant</Tag>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>FERPA Compliance</span>
                            <Tag type="cyan">92% Compliant</Tag>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>ISO 27001</span>
                            <Tag type="green">96% Compliant</Tag>
                          </div>
                        </Stack>
                      </Tile>
                    </Column>
                    <Column lg={8}>
                      <Tile style={{ padding: 'var(--cds-spacing-05)' }}>
                        <Heading size="md" style={{ marginBottom: 'var(--cds-spacing-05)' }}>
                          Recent Compliance Activities
                        </Heading>
                        <Stack gap={3}>
                          <div style={{ fontSize: 'var(--cds-body-01-font-size)' }}>
                            <p>• Policy update: GDPR data retention rules</p>
                            <p
                              style={{
                                color: 'var(--cds-text-secondary)',
                                fontSize: 'var(--cds-helper-text-01-font-size)',
                              }}
                            >
                              2 hours ago
                            </p>
                          </div>
                          <div style={{ fontSize: 'var(--cds-body-01-font-size)' }}>
                            <p>• Violation resolved: Data access audit</p>
                            <p
                              style={{
                                color: 'var(--cds-text-secondary)',
                                fontSize: 'var(--cds-helper-text-01-font-size)',
                              }}
                            >
                              1 day ago
                            </p>
                          </div>
                          <div style={{ fontSize: 'var(--cds-body-01-font-size)' }}>
                            <p>• New policy activated: Student data protection</p>
                            <p
                              style={{
                                color: 'var(--cds-text-secondary)',
                                fontSize: 'var(--cds-helper-text-01-font-size)',
                              }}
                            >
                              3 days ago
                            </p>
                          </div>
                        </Stack>
                      </Tile>
                    </Column>
                  </Grid>
                </Stack>
              </div>
            </TabPanel>

            {/* Violation Management Tab */}
            <TabPanel>
              <div style={{ padding: 'var(--cds-spacing-05)' }}>
                <InlineNotification
                  title="Violation Detection & Management"
                  subtitle="Monitor and resolve privacy policy violations with automated detection and remediation workflows."
                  kind="warning"
                  lowContrast
                  style={{ marginBottom: 'var(--cds-spacing-05)' }}
                />

                <DataTable headers={violationTableHeaders} rows={violationTableRows}>
                  {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                    <TableContainer>
                      <Table {...getTableProps()}>
                        <TableHead>
                          <TableRow>
                            {headers.map(header => (
                              <TableHeader {...getHeaderProps({ header })} key={header.key}>
                                {header.header}
                              </TableHeader>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.map(row => (
                            <TableRow {...getRowProps({ row })} key={row.id}>
                              {row.cells.map(cell => (
                                <TableCell key={cell.id}>{cell.value}</TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </DataTable>
              </div>
            </TabPanel>

            {/* Settings Tab */}
            <TabPanel>
              <div style={{ padding: 'var(--cds-spacing-05)' }}>
                <Stack gap={6}>
                  <InlineNotification
                    title="Privacy Management Settings"
                    subtitle="Configure global settings for privacy policy enforcement and notifications."
                    kind="info"
                    lowContrast
                  />

                  <Tile style={{ padding: 'var(--cds-spacing-05)' }}>
                    <Heading size="md" style={{ marginBottom: 'var(--cds-spacing-05)' }}>
                      Enforcement Settings
                    </Heading>
                    <Stack gap={4}>
                      <Toggle
                        id="auto-enforcement"
                        labelText="Enable automatic policy enforcement"
                        defaultToggled={true}
                      />
                      <Toggle
                        id="real-time-monitoring"
                        labelText="Real-time violation monitoring"
                        defaultToggled={true}
                      />
                      <Toggle
                        id="compliance-reports"
                        labelText="Automated compliance reporting"
                        defaultToggled={false}
                      />
                    </Stack>
                  </Tile>

                  <Tile style={{ padding: 'var(--cds-spacing-05)' }}>
                    <Heading size="md" style={{ marginBottom: 'var(--cds-spacing-05)' }}>
                      Notification Preferences
                    </Heading>
                    <Stack gap={4}>
                      <Checkbox
                        id="violation-alerts"
                        labelText="Email alerts for policy violations"
                        defaultChecked={true}
                      />
                      <Checkbox
                        id="policy-updates"
                        labelText="Notifications for policy updates"
                        defaultChecked={true}
                      />
                      <Checkbox
                        id="weekly-reports"
                        labelText="Weekly compliance summary reports"
                        defaultChecked={false}
                      />
                    </Stack>
                  </Tile>
                </Stack>
              </div>
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* Create Policy Modal */}
        <Modal
          open={isCreatePolicyModalOpen}
          onRequestClose={() => setIsCreatePolicyModalOpen(false)}
          modalHeading="Create New Privacy Policy"
          modalLabel="Enterprise Privacy Management"
          primaryButtonText="Create Policy"
          secondaryButtonText="Cancel"
          onRequestSubmit={handleCreatePolicy}
          primaryButtonDisabled={!newPolicy.name || !newPolicy.description || operationInProgress}
          hasScrollingContent
        >
          <Form>
            <FormGroup legendText="Policy Details">
              <Stack gap={5}>
                <TextInput
                  id="policy-name"
                  labelText="Policy Name"
                  placeholder="e.g., GDPR Compliance Policy"
                  value={newPolicy.name}
                  onChange={e => setNewPolicy({ ...newPolicy, name: e.target.value })}
                />
                <TextArea
                  id="policy-description"
                  labelText="Description"
                  placeholder="Describe the purpose and scope of this privacy policy..."
                  value={newPolicy.description}
                  onChange={e => setNewPolicy({ ...newPolicy, description: e.target.value })}
                  rows={4}
                />
              </Stack>
            </FormGroup>

            <FormGroup legendText="Compliance Frameworks">
              <Checkbox
                id="gdpr-framework"
                labelText="GDPR (General Data Protection Regulation)"
                onChange={checked => {
                  const frameworks = checked
                    ? [...newPolicy.compliance_frameworks, 'GDPR']
                    : newPolicy.compliance_frameworks.filter(f => f !== 'GDPR');
                  setNewPolicy({ ...newPolicy, compliance_frameworks: frameworks });
                }}
              />
              <Checkbox
                id="coppa-framework"
                labelText="COPPA (Children's Online Privacy Protection Act)"
                onChange={checked => {
                  const frameworks = checked
                    ? [...newPolicy.compliance_frameworks, 'COPPA']
                    : newPolicy.compliance_frameworks.filter(f => f !== 'COPPA');
                  setNewPolicy({ ...newPolicy, compliance_frameworks: frameworks });
                }}
              />
              <Checkbox
                id="ferpa-framework"
                labelText="FERPA (Family Educational Rights and Privacy Act)"
                onChange={checked => {
                  const frameworks = checked
                    ? [...newPolicy.compliance_frameworks, 'FERPA']
                    : newPolicy.compliance_frameworks.filter(f => f !== 'FERPA');
                  setNewPolicy({ ...newPolicy, compliance_frameworks: frameworks });
                }}
              />
            </FormGroup>

            <FormGroup legendText="Enforcement Options">
              <Toggle
                id="auto-enforcement-new"
                labelText="Enable automatic enforcement"
                defaultToggled={newPolicy.auto_enforcement}
                onToggle={checked => setNewPolicy({ ...newPolicy, auto_enforcement: checked })}
              />
            </FormGroup>
          </Form>
        </Modal>
      </div>
    </Theme>
  );
};

export default EnterprisePrivacy;
